import { FieldDisplayType } from "./types";
import { getFieldDisplayTypeLabel } from "./hooks";
import { RESTRICTED_FIELD_TYPES } from "./const";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import { TFunction } from "i18next";

export const getFieldDisplayTypeHelpText = (
  fieldType: FieldDisplayType | undefined,
  t: (key: string) => string
): string => {
  if (!fieldType) return "";

  const fieldTypesWithHelpText = [
    FieldDisplayType.EMAIL,
    FieldDisplayType.URL,
    FieldDisplayType.PERCENTAGE
  ];

  if (!fieldTypesWithHelpText.includes(fieldType)) return "";

  const helpTextKey = `fieldTypeHelpTexts.${fieldType}`;
  return t(`settings.deals.fieldManagement.${helpTextKey}`);
};

export const generateFieldDisplayTypeOptions = (
  t: TFunction<"translation", undefined>,
  includeWorkflowTypes: boolean = false,
  isChecklistItemEnabled: boolean = false,
  isPartnerContactsEnabled: boolean = false,
  isFileUploadEnabled: boolean = false
) => {
  const workflowOnlyTypes = [
    FieldDisplayType.BOOL,
    FieldDisplayType.CHECKBOX_WITH_REQUIRED_TEXT,
    FieldDisplayType.PEOPLE_IN_COMPANY
  ];

  const allFieldTypes = Object.values(FieldDisplayType)
    .filter((type) => {
      if (type === FieldDisplayType.CHECKLIST_ITEM && !isChecklistItemEnabled) {
        return false;
      }
      if (type === FieldDisplayType.FILE_UPLOAD && !isFileUploadEnabled) {
        return false;
      }
      if (type === FieldDisplayType.PARTNER_CONTACTS) {
        return isPartnerContactsEnabled;
      }
      if (workflowOnlyTypes.includes(type)) {
        return includeWorkflowTypes;
      }
      if (type === FieldDisplayType.PEOPLE) {
        return false;
      }
      return !RESTRICTED_FIELD_TYPES.includes(type);
    })
    .map((type) => ({
      id: type,
      displayName: getFieldDisplayTypeLabel(type, t)
    }));

  return allFieldTypes;
};

export interface FieldTypeOption {
  id: FieldDisplayType;
  displayName: string;
  disabled?: boolean;
  subtitle?: string;
}

export const restrictFieldTypeOptions = (
  options: FieldTypeOption[],
  currentDisplayType: FieldDisplayType | undefined,
  allowedDisplayTypes: FieldDisplayType[],
  blockedSubtitle: string
): FieldTypeOption[] =>
  options.map((option) => {
    const isAllowedTarget =
      option.id === currentDisplayType ||
      allowedDisplayTypes.includes(option.id);
    return isAllowedTarget
      ? option
      : {
          ...option,
          disabled: true,
          subtitle: blockedSubtitle
        };
  });

export const getWorkflowColumnTitle = (
  workflowType: QuestionnaireType,
  t: (key: string) => string
): string => {
  return t(
    `settings.deals.fieldManagement.workflowColumnTitle.${workflowType}`
  );
};
